import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

import { Navigation } from "@/components/Navigation";
import { PublicFooter } from "@/components/PublicFooter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { getBrandConfig } from "@/config/brand";
import { companyService } from "@/services/companyService";
import { lowercaseEmailSchema } from "@/lib/emailSchema";
import {
  appendThreeDigits,
  randomThreeDigits,
  subdomainFromCompanyName,
} from "@/lib/signupSubdomain";

type RegisterFormValues = {
  company_name: string;
  admin_name: string;
  admin_email: string;
  password: string;
  confirm_password: string;
};

const DUPLICATE_SUBDOMAIN_DETAIL = "Subdomain already in use";
const MAX_SUBDOMAIN_ATTEMPTS = 5;

const getErrorMessage = (error: unknown, fallback: string) => {
  if (error instanceof Error && error.message) return error.message;
  if (
    typeof error === "object" &&
    error !== null &&
    "response" in error &&
    typeof (error as { response?: { data?: { detail?: unknown } } }).response?.data
      ?.detail === "string"
  ) {
    return (error as { response: { data: { detail: string } } }).response.data.detail;
  }
  return fallback;
};

const Register = () => {
  const { t } = useTranslation();
  const brand = getBrandConfig();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const affiliateToken = searchParams.get("affiliate") ?? undefined;
  const [isSubmitting, setIsSubmitting] = useState(false);

  const registerSchema = useMemo(
    () =>
      z
        .object({
          company_name: z.string().trim().min(2, t("auth.register.validation.companyNameMin")),
          admin_name: z.string().trim().min(1, t("auth.register.validation.nameRequired")),
          admin_email: lowercaseEmailSchema(
            t("auth.invalidEmail"),
            t("auth.lowercaseEmail"),
          ),
          password: z.string().min(8, t("auth.register.validation.passwordMin")),
          confirm_password: z.string().min(1, t("auth.register.validation.confirmRequired")),
        })
        .refine((values) => values.password === values.confirm_password, {
          message: t("auth.register.validation.passwordMismatch"),
          path: ["confirm_password"],
        }),
    [t],
  );

  const form = useForm<RegisterFormValues>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      company_name: "",
      admin_name: "",
      admin_email: "",
      password: "",
      confirm_password: "",
    },
  });

  const onSubmit = async (data: RegisterFormValues) => {
    setIsSubmitting(true);
    try {
      const baseSubdomain = subdomainFromCompanyName(data.company_name);
      const signup = (subdomain: string) =>
        companyService.signupCompany({
          company_name: data.company_name,
          subdomain,
          admin_name: data.admin_name,
          admin_email: data.admin_email,
          password: data.password,
          industry: null,
          department: null,
          affiliate_token: affiliateToken,
        });

      const response = await signup(baseSubdomain).catch(async (error: unknown) => {
        if (getErrorMessage(error, "") !== DUPLICATE_SUBDOMAIN_DETAIL) {
          throw error;
        }
        let lastError: unknown = error;
        for (let attempt = 1; attempt < MAX_SUBDOMAIN_ATTEMPTS; attempt += 1) {
          try {
            return await signup(appendThreeDigits(baseSubdomain, randomThreeDigits()));
          } catch (retryError: unknown) {
            lastError = retryError;
            if (getErrorMessage(retryError, "") !== DUPLICATE_SUBDOMAIN_DETAIL) {
              throw retryError;
            }
          }
        }
        throw lastError;
      });

      toast.success(t("auth.register.toast.workspaceCreated"), {
        description: t("auth.register.toast.workspaceCreatedDescription", {
          count: response.credit_balance,
        }),
      });
      navigate(response.next_url || "/login");
    } catch (error: unknown) {
      toast.error(t("auth.register.toast.signupFailed"), {
        description: getErrorMessage(error, t("auth.register.toast.signupFailedDescription")),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden flex flex-col">
      <div className="absolute inset-0 gradient-ai-subtle opacity-30" />
      <div className="absolute inset-0 bg-grid-pattern opacity-5" />

      <Navigation />

      <div className="flex flex-1 items-center justify-center p-4">
        <Card className="w-full max-w-md glass relative">
          <CardHeader className="space-y-1 text-center">
            <div className="mx-auto mb-4 flex items-center justify-center">
              <img src={brand.paths.logo} alt={brand.logoAlt} className="h-16 w-auto object-contain" />
            </div>
            <CardTitle className="text-2xl font-bold">{t("auth.register.createWorkspaceTitle")}</CardTitle>
            <CardDescription>{t("auth.register.createWorkspaceSubtitle")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {affiliateToken ? (
              <Alert>
                <AlertDescription>
                  {t("auth.register.referralAlert")}
                </AlertDescription>
              </Alert>
            ) : null}
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="company_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("auth.register.companyName")}</FormLabel>
                      <FormControl>
                        <Input placeholder={t("auth.register.placeholders.companyName")} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="admin_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("auth.register.yourName")}</FormLabel>
                      <FormControl>
                        <Input placeholder={t("auth.register.placeholders.adminName")} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="admin_email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("auth.register.workEmail")}</FormLabel>
                      <FormControl>
                        <Input type="email" placeholder={t("auth.register.placeholders.email")} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("auth.password")}</FormLabel>
                      <FormControl>
                        <Input type="password" autoComplete="new-password" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="confirm_password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("auth.register.confirmPassword")}</FormLabel>
                      <FormControl>
                        <Input type="password" autoComplete="new-password" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button className="w-full gradient-ai" type="submit" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t("auth.register.creatingWorkspace")}
                    </>
                  ) : (
                    t("auth.register.createWorkspace")
                  )}
                </Button>
              </form>
            </Form>

            <p className="text-center text-sm text-muted-foreground">
              {t("auth.register.alreadyHaveAccount")}{" "}
              <Link to="/login" className="text-primary hover:underline">
                {t("common.signIn")}
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>

      <PublicFooter />
    </div>
  );
};

export default Register;
